import { useEffect, useState } from "react";
import { Button } from "@/shadcnComponents/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shadcnComponents/ui/dropdown-menu";
import { ChevronDown, Download } from "lucide-react";
import type {
  TransactionType,
  CreditTransaction,
  PaymentTransaction,
} from "../paymentHistoryLib/paymentHistoryTypes";
import { SharedTransactionTable } from "./shared-transaction-table";
import { useBrand } from "@/Providers/Brand";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";
import { useGeneralSettings } from "@/Providers/General";

const ITEMS_PER_PAGE = 4;

type TransactionView = "credits" | "payments";

export default function TransactionHistory() {
  const brandState = useBrand();
  const generalState = useGeneralSettings();
  const [view, setView] = useState<TransactionView>("credits");
  const [transactionTypeFilter, setTransactionTypeFilter] =
    useState<TransactionType | null>(null);
  const [creditTypeFilter, setCreditTypeFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [creditTransactions, setCreditsTransactions] = useState<
    CreditTransaction[]
  >([]);
  const [paymentTransactions, setPaymentTransactions] = useState([]);

  const creditColumns = [
    { header: "Type", accessor: "type" as const },
    {
      header: "Transaction Type",
      accessor: (transaction: CreditTransaction) => (
        <span
          className={`inline-block px-2 py-1 rounded-full text-sm ${
            transaction.transactionType === "Credit"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {transaction.transactionType}
        </span>
      ),
    },
    { header: "Campaign Name/Plan Name", accessor: "name" as const },
    { header: "Credits", accessor: "credits" as const },
    { header: "Timestamp", accessor: "timestamp" as const },
  ];

  const paymentColumns = [
    { header: "Date", accessor: "date" as const },
    { header: "Txn ID", accessor: "txnId" as const },
    { header: "Amount", accessor: (t: PaymentTransaction) => `₹ ${t.amount}` },
    {
      header: "Status",
      accessor: (t: PaymentTransaction) => (
        <span
          className={`inline-block px-2 py-1 rounded-full text-sm ${
            t.status === "Success"
              ? "bg-green-100 text-green-800"
              : t.status === "Pending"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {t.status}
        </span>
      ),
    },
    {
      header: "Payment Method",
      accessor: (t: PaymentTransaction) => {
        return t.mode ?? "-";
      },
    },
    { header: "Subject", accessor: "subject" as const },
    { header: "Invoice Number", accessor: "invoiceNumber" as const },
    {
      header: "Download Invoice",
      accessor: (t: PaymentTransaction) => (
        <>
          {t.invoice ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      generalState?.handleDownloadPdf(t.invoice);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download Invoice</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            "N/A"
          )}
        </>
      ),
    },
  ];

  const filteredCreditTransactions = creditTransactions.filter(
    (transaction) => {
      const creditTypeFilterMatch =
        !creditTypeFilter || transaction.type === creditTypeFilter;
      const transactionTypeFilterMatch =
        !transactionTypeFilter ||
        transaction.transactionType === transactionTypeFilter;
      return creditTypeFilterMatch && transactionTypeFilterMatch;
    }
  );

  const totalPages = Math.ceil(
    (view === "credits" ? filteredCreditTransactions : paymentTransactions)
      .length / ITEMS_PER_PAGE
  );

  const paginatedData = (
    view === "credits" ? filteredCreditTransactions : paymentTransactions
  ).slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  //effects
  useEffect(() => {
    const getCreditTransactions = async () => {
      const result = await brandState?.getTransactionHistory();
      if (result.success) {
        setCreditsTransactions(result.data.transactions);
      } else {
        console.error(result.error);
      }
    };
    getCreditTransactions();
  }, [brandState]);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const paymentHistory = await brandState?.getBrandPaymentTransactions();
        if (paymentHistory?.success) {
          setPaymentTransactions(paymentHistory?.transactions);
        }
      } catch (error) {
        console.log("error while fetching payment history: ", error);
      }
    };
    fetchPaymentHistory();
  }, [brandState]);

  return (
    <>
      <div className="w-full border-b">
        <div className="max-w-6xl mx-auto py-4 px-4">
          <div className="flex gap-2">
            <Button
              variant={view === "credits" ? "default" : "outline"}
              onClick={() => setView("credits")}
              size="lg"
            >
              Credits Transaction
            </Button>
            <Button
              variant={view === "payments" ? "default" : "outline"}
              onClick={() => setView("payments")}
              size="lg"
            >
              Payment Transaction
            </Button>
          </div>
        </div>
      </div>

      <div className="w-full max-w-6xl mx-auto p-4 space-y-6">
        <div className="flex flex-col gap-6">
          {view === "credits" && (
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full">
                    Credit Type <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setCreditTypeFilter(null)}>
                    All
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCreditTypeFilter("Campaign")}
                  >
                    Campaign Credits
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCreditTypeFilter("Collaboration")}
                  >
                    Collaboration Credits
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setCreditTypeFilter("Anchors Credits")}
                  >
                    Anchors Credits
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full">
                    Transaction Type <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={() => setTransactionTypeFilter(null)}
                  >
                    All
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTransactionTypeFilter("Credit")}
                  >
                    Credit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTransactionTypeFilter("Debit")}
                  >
                    Debit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTransactionTypeFilter("Expired")}
                  >
                    Expired
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {view === "credits" ? (
            <SharedTransactionTable<CreditTransaction>
              data={paginatedData as CreditTransaction[]}
              columns={creditColumns}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          ) : (
            <SharedTransactionTable<PaymentTransaction>
              data={paginatedData as PaymentTransaction[]}
              columns={paymentColumns}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </>
  );
}
